/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useEffect, useContext } from 'react';
import LayoutContainer from 'components/layout/LayoutContainer';
import AdminHeader from 'components/header/index';
import Card from 'components/inputs/Card';
import { Row, Col } from 'react-bootstrap';
import TextInput from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import ToggleSwitch from 'components/inputs/ToggleSwitch';
import Button from 'components/inputs/Button';
import * as _ from 'lodash';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';
import { BsUpload } from 'react-icons/bs';
import { FiDownload } from 'react-icons/fi';
import { NavContext } from 'components/privateRoute/PrivateRoute';
import { CREATE_STATUS_CODE, UPDATE_STATUS_CODE } from 'components/common/constant';
import {
  CREATE_PRELOADED_CAMPAIGN,
  UPDATE_PRELOADED_CAMPAIGN,
  GET_PRELOADED_CAMPAIGN,
} from 'actions/preloadedCampaign';
import { FETCH_USER_GROUP, CREATE_BULK_USERS_TO_USER_GROUP } from 'actions/userGroup';

const toDateInput = (d) => (d ? moment(d).format('YYYY-MM-DD') : '');

// Policy rules — fixed per client sign-off (Q1-Q7). Not admin-configurable; sent
// with every campaign so behaviour is explicit and consistent.
const FIXED_POLICY = {
  percentage_base: 'gross', // Q2 — M% on gross bill incl GST
  rounding: 'floor', // Q2 — rounded down to nearest ₹1
  decrement_n_on: 'applied', // Q1 — N counts only when credit applied
  stack_with_offers: false, // Q3 — never with session-level offers
  stack_with_chargecoin: true, // Q3 — always allowed with ChargeCoin
  exclude_discounted_stations: false, // Q3 — discounted stations allowed
  exclude_tata_ev: true, // Q6 — Tata.ev excluded
  include_owned_stations: true, // Q5 — ChargeZone-owned stations always included
  expire_leftover_on_n: true, // Q4 — reverse leftover immediately on N
  restore_on_refund: true, // Q7 — restore credit on full refund
  refund_count_session_min_utilised: 5, // Q7 — ₹5 partial-refund threshold
};

const PreloadedCampaignForm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { campaignId } = useParams();
  const { navigateTo } = useContext(NavContext);
  const isEdit = !!campaignId;

  const campaignDetail = useSelector((state) => state.preloadedCampaign.preloadedCampaignDetail);
  const allUserGroup = useSelector((state) => state.userGroup.userGroups);
  const userGroupPage = useSelector((state) => state.userGroup.page);
  const userGroupTotalPage = useSelector((state) => state.userGroup.totalPages);

  const getAllUserGroup = useCallback((data = {}) => {
    dispatch({ type: FETCH_USER_GROUP.REQUEST, payload: data });
  }, []);

  useEffect(() => {
    getAllUserGroup();
    if (isEdit) {
      dispatch({ type: GET_PRELOADED_CAMPAIGN.REQUEST, payload: campaignId });
    }
  }, [campaignId]);

  const initialValues = isEdit
    ? {
        name: _.get(campaignDetail, 'name', ''),
        campaign_code: _.get(campaignDetail, 'campaign_code', ''),
        amount: _.get(campaignDetail, 'amount', ''),
        max_sessions: _.get(campaignDetail, 'max_sessions', ''),
        percentage_per_session: _.get(campaignDetail, 'percentage_per_session', ''),
        min_units: _.get(campaignDetail, 'min_units', 0),
        start_date: toDateInput(_.get(campaignDetail, 'start_date')),
        end_date: toDateInput(_.get(campaignDetail, 'end_date')),
        cohort_group: _.get(campaignDetail, 'cohort_group.id') || _.get(campaignDetail, 'cohort_group', ''),
        eligible_stations: _.map(_.get(campaignDetail, 'eligible_stations', []), (s) => s.id || s),
        is_active: _.get(campaignDetail, 'is_active', false),
      }
    : {
        name: '',
        campaign_code: '',
        amount: '',
        max_sessions: 4,
        percentage_per_session: 10,
        min_units: 5,
        start_date: '',
        end_date: '',
        cohort_group: '',
        eligible_stations: [],
        is_active: false,
      };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    amount: Yup.number().moreThan(0, 'Amount must be > 0').required('Amount is required'),
    max_sessions: Yup.number().integer().moreThan(0, 'N must be > 0').required('N is required'),
    percentage_per_session: Yup.number().moreThan(0).max(100).required('M% is required'),
    min_units: Yup.number().min(0).required('X is required'),
    start_date: Yup.date().required('Start date is required'),
    end_date: Yup.date().min(Yup.ref('start_date'), 'End date must be on or after the start date').required('End date is required'),
    cohort_group: Yup.string().required('Cohort user group is required'),
  });

  const onSubmit = useCallback(
    (values) => {
      const payload = {
        name: values.name,
        campaign_code: values.campaign_code,
        amount: Number(values.amount),
        max_sessions: Number(values.max_sessions),
        percentage_per_session: Number(values.percentage_per_session),
        min_units: Number(values.min_units),
        // Send the raw calendar day (YYYY-MM-DD). The backend snaps start → 00:00
        // IST and end → 23:59:59.999 IST, so a single-day campaign (14 Jul → 14
        // Jul) runs the full day and boundaries align to IST midnight. (Mirrors
        // the CDB-300 date-range handling.)
        start_date: values.start_date,
        end_date: values.end_date,
        cohort_group: values.cohort_group,
        eligible_stations: values.eligible_stations || [],
        // on/off is driven by the Active toggle; keep status in sync for reporting
        is_active: values.is_active,
        status: values.is_active ? 'active' : 'draft',
        ...FIXED_POLICY,
      };
      const done = (res, code) => {
        if (_.get(res, 'status') === code) navigateTo('/preloadedCampaigns');
      };
      if (isEdit) {
        payload.id = campaignId;
        dispatch({ type: UPDATE_PRELOADED_CAMPAIGN.REQUEST, payload, cb: (r) => done(r, UPDATE_STATUS_CODE) });
      } else {
        dispatch({ type: CREATE_PRELOADED_CAMPAIGN.REQUEST, payload, cb: (r) => done(r, CREATE_STATUS_CODE) });
      }
    },
    [isEdit, campaignId]
  );

  // ---- Cohort Excel upload (reuses the UserGroup bulk-upload flow) ----
  const uploadCohort = (groupId) => {
    if (!groupId) {
      toast.error('Select a cohort user group first');
      return;
    }
    const fileUpload = document.getElementById('cohortFile');
    const regex = /^([a-zA-Z0-9\s_\\.\-:()])+(.xls|.xlsx)$/;
    if (!regex.test(fileUpload.value.toLowerCase())) {
      toast.error('Please upload a valid Excel file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const workbook = XLSX.read(reader.result, { type: 'binary' });
      const firstSheet = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[firstSheet]);
      dispatch({
        type: CREATE_BULK_USERS_TO_USER_GROUP.REQUEST,
        payload: { userData: rows, id: groupId },
        cb: (res) => {
          if (_.get(res, 'status') === UPDATE_STATUS_CODE) {
            toast.success(`Cohort uploaded (${rows.length} rows) to the selected group`);
          }
        },
      });
    };
    reader.readAsBinaryString(fileUpload.files[0]);
  };

  // ---- Eligible-stations Excel upload (populates campaign.eligible_stations) ----
  const uploadStations = (setFieldValue) => {
    const fileUpload = document.getElementById('stationFile');
    const regex = /^([a-zA-Z0-9\s_\\.\-:()])+(.xls|.xlsx)$/;
    if (!regex.test(fileUpload.value.toLowerCase())) {
      toast.error('Please upload a valid Excel file.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const workbook = XLSX.read(reader.result, { type: 'binary' });
      const firstSheet = workbook.SheetNames[0];
      const rows = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[firstSheet]);
      const ids = _.uniq(
        rows.map((r) => String(r.station_id || r.id || r.stationId || '').trim()).filter((id) => id.length > 0)
      );
      setFieldValue('eligible_stations', ids);
      toast.success(`${ids.length} station id(s) loaded`);
    };
    reader.readAsBinaryString(fileUpload.files[0]);
  };

  const downloadSample = (sample, fileName) => {
    const ws = XLSX.utils.json_to_sheet(sample);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    FileSaver.saveAs(blob, fileName);
  };

  const sectionTitle = { fontSize: 14, fontWeight: 600, opacity: 0.9, margin: '18px 0 4px' };
  const fileInputHidden = { position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden' };
  const uploadBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
    padding: '10px 16px', borderRadius: 8, border: '1px dashed #4a5a6a', color: '#cdd7e0', fontSize: 13,
  };
  const linkBtn = {
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: '#58d37d', fontSize: 13,
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <AdminHeader
          title={isEdit ? t('header.editPreloadedCampaign', 'Edit Campaign') : t('header.addPreloadedCampaign', 'Add Campaign')}
          backTo="/preloadedCampaigns"
        />
        <div className="page-content-wrapper scrollable">
          <Card>
            <div className="p-4">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
                  <Form onSubmit={handleSubmit}>
                    {/* Top bar: Active/Inactive toggle */}
                    <div
                      style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        paddingBottom: 16, marginBottom: 12, borderBottom: '1px solid #2a3a48',
                      }}
                    >
                      <div style={{ fontSize: 16, fontWeight: 600 }}>Campaign details</div>
                      <ToggleSwitch
                        id="is_active"
                        name="is_active"
                        checked={values.is_active}
                        onChange={(e) => setFieldValue('is_active', e.target.checked)}
                        labelClassName={values.is_active ? 'text-success' : 'text-muted'}
                      >
                        {values.is_active ? 'Active' : 'Inactive'}
                      </ToggleSwitch>
                    </div>

                    <Row>
                      <Col lg={6}>
                        <TextInput label="Name" name="name" value={values.name} onChange={handleChange}
                          error={errors.name && touched.name ? errors.name : null} />
                      </Col>
                      <Col lg={6}>
                        <TextInput label="Campaign Code" name="campaign_code" value={values.campaign_code} onChange={handleChange} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="Credit Amount (₹)" name="amount" value={values.amount} onChange={handleChange}
                          error={errors.amount && touched.amount ? errors.amount : null} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="Max Sessions (N)" name="max_sessions" value={values.max_sessions} onChange={handleChange}
                          error={errors.max_sessions && touched.max_sessions ? errors.max_sessions : null} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="% per Session (M)" name="percentage_per_session" value={values.percentage_per_session} onChange={handleChange}
                          error={errors.percentage_per_session && touched.percentage_per_session ? errors.percentage_per_session : null} />
                      </Col>
                      <Col lg={3}>
                        <TextInput type="number" label="Min Units (X, kWh)" name="min_units" value={values.min_units} onChange={handleChange}
                          error={errors.min_units && touched.min_units ? errors.min_units : null} />
                      </Col>
                      <Col lg={6}>
                        <TextInput type="date" label="Start Date" name="start_date" value={values.start_date} onChange={handleChange}
                          max={values.end_date || undefined}
                          error={errors.start_date && touched.start_date ? errors.start_date : null} />
                      </Col>
                      <Col lg={6}>
                        <TextInput type="date" label="End Date" name="end_date" value={values.end_date} onChange={handleChange}
                          min={values.start_date || undefined}
                          error={errors.end_date && touched.end_date ? errors.end_date : null} />
                      </Col>
                    </Row>

                    {/* ---- Cohort (churned customers) ---- */}
                    <div style={sectionTitle}>Cohort (churned customers)</div>
                    <Row className="align-items-end">
                      <Col lg={6}>
                        <Select
                          label="Cohort User Group"
                          placeholder="Select cohort"
                          options={_.map(allUserGroup, (item) => ({ label: item.name, value: item.id }))}
                          value={values.cohort_group}
                          name="cohort_group"
                          onMenuScrollDown={true}
                          page={userGroupPage}
                          totalPage={userGroupTotalPage}
                          getDataOnScrollDown={getAllUserGroup}
                          onChange={(val) => setFieldValue('cohort_group', val)}
                          error={errors.cohort_group && touched.cohort_group ? errors.cohort_group : null}
                        />
                      </Col>
                      <Col lg={6}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 8 }}>
                          <label htmlFor="cohortFile" style={uploadBtn}>
                            <BsUpload /> {' '}Upload cohort (Excel)
                            <input type="file" id="cohortFile" style={fileInputHidden}
                              onChange={() => uploadCohort(values.cohort_group)} />
                          </label>
                          <span style={linkBtn} onClick={() => downloadSample([{ phone: '', country_code: '' }], 'cohort_sample.xlsx')}>
                            <FiDownload /> Download sample
                          </span>
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.6 }}>
                          Uploading adds these phones into the selected group.
                        </div>
                      </Col>
                    </Row>

                    {/* ---- Eligible stations ---- */}
                    <div style={sectionTitle}>Eligible stations</div>
                    <Row className="align-items-center">
                      <Col lg={6}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                          <label htmlFor="stationFile" style={uploadBtn}>
                            <BsUpload /> {' '}Upload station IDs (Excel)
                            <input type="file" id="stationFile" style={fileInputHidden}
                              onChange={() => uploadStations(setFieldValue)} />
                          </label>
                          <span style={linkBtn} onClick={() => downloadSample([{ station_id: '' }], 'eligible_stations_sample.xlsx')}>
                            <FiDownload /> Download sample
                          </span>
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div style={{ fontSize: 13, opacity: 0.8 }}>
                          {(values.eligible_stations || []).length > 0
                            ? `${values.eligible_stations.length} station(s) selected — offer shows only on these`
                            : 'No stations uploaded → offer eligible on all campaign chargers'}
                        </div>
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4" style={{ gap: 12 }}>
                      <Button className="cancel_btn" onClick={() => navigateTo('/preloadedCampaigns')}>
                        {t('button.cancel', 'Cancel')}
                      </Button>
                      <Button type="submit" className="save-btn">
                        {isEdit ? t('button.update', 'Update') : t('button.save', 'Save')}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </Card>
        </div>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default PreloadedCampaignForm;
